import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    displaceSvgContent,
    type DisplacementParams,
    type DisplacementMode,
    type ChannelMode,
    type NoiseDisplacementStyle,
} from "../../utils/displacementMap";
import { useSvgToolHandoffReceiver } from "../../hooks/useSvgToolHandoffReceiver";
import { extractViewBox, isValidSvg } from "../../utils/svgUtils";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";

interface UploadedSvg {
    name: string;
    content: string;
}

interface MapImage {
    name: string;
    width: number;
    height: number;
}

interface DisplacementMapState {
    // SVG file
    svg: UploadedSvg | null;
    svgError: string | null;
    svgInputRef: React.RefObject<HTMLInputElement>;
    handleSvgLoad: (e: React.ChangeEvent<HTMLInputElement>) => void;
    // Mode
    mode: DisplacementMode;
    setMode: (v: DisplacementMode) => void;
    // Noise params
    seed: string;
    noiseScale: number;
    octaves: number;
    noiseStyle: NoiseDisplacementStyle;
    setSeed: (v: string) => void;
    setNoiseScale: (v: number) => void;
    setOctaves: (v: number) => void;
    setNoiseStyle: (v: NoiseDisplacementStyle) => void;
    // Image params
    mapImage: MapImage | null;
    mapImageError: string | null;
    mapImageInputRef: React.RefObject<HTMLInputElement>;
    handleMapImageLoad: (e: React.ChangeEvent<HTMLInputElement>) => void;
    channelMode: ChannelMode;
    setChannelMode: (v: ChannelMode) => void;
    // Shared params
    strength: number;
    circleSegments: number;
    setStrength: (v: number) => void;
    setCircleSegments: (v: number) => void;
    // Derived
    viewBoxAttr: string;
    displacedSvgContent: string | null;
    canExport: boolean;
    handleDownload: () => void;
    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
}

const DisplacementMapContext = createContext<DisplacementMapState | null>(null);

const DEFAULT_DISPLACEMENT_MAP_PRESET: Record<string, unknown> = {
    mode: "noise",
    seed: "map",
    noiseScale: 0.005,
    octaves: 3,
    noiseStyle: "fbm",
    strength: 20,
    circleSegments: 48,
    channelMode: "rg",
};

export function useDisplacementMap() {
    const ctx = useContext(DisplacementMapContext);
    if (!ctx)
        throw new Error("useDisplacementMap must be used within DisplacementMapProvider");
    return ctx;
}

export function DisplacementMapProvider({ children }: { children: React.ReactNode }) {
    // SVG file
    const [svg, setSvg] = useState<UploadedSvg | null>(null);
    const [svgError, setSvgError] = useState<string | null>(null);
    const svgInputRef = useRef<HTMLInputElement | null>(null);

    // Mode
    const [mode, setMode] = useState<DisplacementMode>("noise");

    // Noise params
    const [seed, setSeed] = useState("map");
    const [noiseScale, setNoiseScale] = useState(0.005);
    const [octaves, setOctaves] = useState(3);
    const [noiseStyle, setNoiseStyle] = useState<NoiseDisplacementStyle>("fbm");

    // Image params
    const [mapImageUrl, setMapImageUrl] = useState<string | null>(null);
    const [mapImage, setMapImage] = useState<MapImage | null>(null);
    const [mapImageData, setMapImageData] = useState<ImageData | null>(null);
    const [mapImageError, setMapImageError] = useState<string | null>(null);
    const [channelMode, setChannelMode] = useState<ChannelMode>("rg");
    const mapImageInputRef = useRef<HTMLInputElement | null>(null);
    const prevMapImageUrl = useRef<string | null>(null);

    // Shared params
    const [strength, setStrength] = useState(20);
    const [circleSegments, setCircleSegments] = useState(48);

    // ── SVG file loader ──────────────────────────────────────────────────────

    const handleSvgLoad = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setSvgError(null);

        if (!file.type.includes("svg") && !file.name.toLowerCase().endsWith(".svg")) {
            setSvgError("Please choose an SVG file.");
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const text = typeof reader.result === "string" ? reader.result : "";
            if (!text) { setSvgError("Unable to read SVG file."); return; }
            if (!isValidSvg(text)) { setSvgError("File does not appear to be valid SVG."); return; }
            setSvg({ name: file.name, content: text });
        };
        reader.onerror = () => setSvgError("Error reading file.");
        reader.readAsText(file);
    }, []);

    // ── Map image loader ─────────────────────────────────────────────────────

    const handleMapImageLoad = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setMapImageError(null);

        if (!file.type.startsWith("image/")) {
            setMapImageError("Please choose an image file (PNG, JPG, WebP, etc.).");
            return;
        }

        // Revoke previous object URL
        if (prevMapImageUrl.current) {
            URL.revokeObjectURL(prevMapImageUrl.current);
        }

        const url = URL.createObjectURL(file);
        prevMapImageUrl.current = url;
        setMapImageUrl(url);
        setMapImageData(null);
        setMapImage(null);
    }, []);

    // Extract ImageData whenever a new map image URL is set
    useEffect(() => {
        if (!mapImageUrl) return;

        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext("2d");
            if (!ctx) {
                setMapImageError("Could not create canvas context.");
                return;
            }
            ctx.drawImage(img, 0, 0);
            try {
                const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
                setMapImageData(data);
                setMapImage({ name: img.src.split("/").pop() ?? "image", width: img.naturalWidth, height: img.naturalHeight });
                setMapImageError(null);
            } catch {
                setMapImageError("Could not read image pixel data.");
            }
        };
        img.onerror = () => setMapImageError("Failed to load image.");
        img.src = mapImageUrl;
    }, [mapImageUrl]);

    // Revoke URL on unmount
    useEffect(() => {
        return () => {
            if (prevMapImageUrl.current) URL.revokeObjectURL(prevMapImageUrl.current);
        };
    }, []);

    // ── Derived values ───────────────────────────────────────────────────────

    const viewBoxAttr = useMemo(() => {
        if (!svg) return "0 0 200 200";
        return extractViewBox(svg.content)?.viewBox ?? "0 0 200 200";
    }, [svg]);

    const viewBoxParts = useMemo(() => {
        const parts = viewBoxAttr.split(/\s+/).map(Number);
        if (parts.length === 4 && parts.every(isFinite)) {
            return { minX: parts[0], minY: parts[1], w: parts[2], h: parts[3] };
        }
        return { minX: 0, minY: 0, w: 200, h: 200 };
    }, [viewBoxAttr]);

    const params = useMemo<DisplacementParams>(
        () => ({
            mode,
            strength,
            circleSegments,
            seed,
            noiseScale,
            octaves,
            noiseStyle,
            imageData: mapImageData,
            channelMode,
            svgMinX: viewBoxParts.minX,
            svgMinY: viewBoxParts.minY,
            svgWidth: viewBoxParts.w,
            svgHeight: viewBoxParts.h,
        }),
        [mode, strength, circleSegments, seed, noiseScale, octaves, noiseStyle,
         mapImageData, channelMode, viewBoxParts],
    );

    const displacedSvgContent = useMemo(() => {
        if (!svg) return null;
        if (mode === "image" && !mapImageData) return null;
        return displaceSvgContent(svg.content, params);
    }, [svg, mode, mapImageData, params]);

    const canExport = !!displacedSvgContent;

    const handleDownload = useCallback(() => {
        if (!displacedSvgContent) return;
        const blob = new Blob([displacedSvgContent], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${svg?.name.replace(/\.svg$/i, "") ?? "displaced"}-warped-${Date.now()}.svg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [displacedSvgContent, svg]);

    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            mode, seed, noiseScale, octaves, noiseStyle, strength, circleSegments, channelMode,
        }),
        [mode, seed, noiseScale, octaves, noiseStyle, strength, circleSegments, channelMode],
    );

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        if (values.mode != null) setMode(values.mode as DisplacementMode);
        if (values.seed != null) setSeed(values.seed as string);
        if (values.noiseScale != null) setNoiseScale(values.noiseScale as number);
        if (values.octaves != null) setOctaves(values.octaves as number);
        if (values.noiseStyle != null) setNoiseStyle(values.noiseStyle as NoiseDisplacementStyle);
        if (values.strength != null) setStrength(values.strength as number);
        if (values.circleSegments != null) setCircleSegments(values.circleSegments as number);
        if (values.channelMode != null) setChannelMode(values.channelMode as ChannelMode);
    }, []);

    const { resetParameters } = useToolParamPersistence({
        toolId: "displacement-map",
        defaultPreset: DEFAULT_DISPLACEMENT_MAP_PRESET,
        getPresetValues,
        applyPreset,
    });

    useSvgToolHandoffReceiver(
        "displacement-map",
        {
            onPrimary: (svg) => {
                setSvgError(null);
                setSvg(svg);
            },
        },
        { setError: setSvgError },
    );

    const value: DisplacementMapState = {
        svg, svgError, svgInputRef, handleSvgLoad,
        mode, setMode,
        seed, noiseScale, octaves, noiseStyle, setSeed, setNoiseScale, setOctaves, setNoiseStyle,
        mapImage, mapImageError, mapImageInputRef, handleMapImageLoad,
        channelMode, setChannelMode,
        strength, circleSegments, setStrength, setCircleSegments,
        viewBoxAttr, displacedSvgContent, canExport, handleDownload,
        applyPreset, getPresetValues, resetParameters,
    };

    return (
        <DisplacementMapContext.Provider value={value}>
            {children}
        </DisplacementMapContext.Provider>
    );
}
